import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";

/**
 * @slot (default) - content of the tab
 * @slot label - can be used instead of the `name` attribute if a tab button should render HTML instead of plain text
 */
@Component({
    tag: "ux-tab",
    styleUrl: "tab.scss",
    shadow: false
})
export class Tab {
    @Element() domNode!: HTMLUxTabElement;

    /** Sets the name for the corresponding tab-button */
    @Prop({ reflect: true }) readonly name: string = "";
    /** Sets ID for Tab */
    @Prop({ reflect: true }) readonly tabId: string = "";
    /** prop that will be set automatically by the ux-tab-container if it
    has the anchor-navigation prop. In this case, all tabs will be visible and
    will be displayed one after the other */
    @Prop() readonly anchorNavigation: boolean = false;

    /** Boolean to indicate active tab */
    @State() active = false;

    /* eslint-disable-next-line @stencil-community/required-jsdoc */
    @Event({
        eventName: "uxTabNameChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    tabNameChange!: EventEmitter;

    @Watch("name")
    emitTabNameChangeEvent() {
        this.tabNameChange.emit({ tabId: this.tabId, tabName: this.name });
    }

    @Listen("uxActiveTabSet", { target: "document" })
    setActiveTab(event: CustomEvent) {
        const eTarget = event.target as HTMLElement;
        // @ts-expect-error Check for typing
        if (this.domNode.parentElement.parentElement.isSameNode(eTarget)) {
            this.active = event.detail === this.tabId;
        } else if (eTarget.contains(this.domNode) && eTarget.closest("ux-form-steps")) {
            this.active = event.detail === this.tabId;
        }
    }

    @State() hasSliderInTab: boolean;

    componentWillLoad() {
        this.hasSliderInTab = this.domNode.querySelector("ux-slider") !== null;
    }

    render() {
        return (
            <Host
                class={{
                    ["ux-tab"]: true,
                    ["is-active"]: this.active,
                    ["ux-tab--anchor-navigation"]: this.anchorNavigation,
                    ["ux-tab--with-slider"]: this.hasSliderInTab
                }}
            >
                <div style={{ display: "none" }}>
                    <slot name="label" />
                </div>
                {/* This div will have 0 height and has the purpose of keeping the
                margins of the content of the tab inside of the tab component,
                without the margins collapsing outside of it */}
                {this.anchorNavigation && <div class="ux-tab__margin-collapse-guard"></div>}
                <slot />
            </Host>
        );
    }
}
